import { useMemo } from 'react';

export type Storefront = 'ch' | 'de' | 'at' | 'it' | 'se' | 'al';
export type Currency = 'CHF' | 'EUR' | 'SEK' | 'ALL';

/**
 * Dedicated render-host alias for the AT storefront. Points at the same
 * published build as bigfollow.lovable.app, but is hard-wired to
 * storefront='at' so the external prerender service can fetch AT snapshots
 * without any query-param trickery. Matched by the wildcard
 * `host.endsWith('.bigfollow.at')` below — listed explicitly so the intent
 * is documented and the wildcard is not accidentally narrowed later.
 * DO NOT emit noindex on this host: snapshots are served 1:1 for
 * bigfollow.at; the canonical (rewritten to https://bigfollow.at) is what
 * prevents duplicate indexing.
 */
export const AT_RENDER_HOST = 'render-at.bigfollow.at';
// Duplicated literal (not imported from useLanguage) to avoid a circular dep:
// useLanguage may depend on useStorefront, never the reverse.
type StorefrontLocale = 'de' | 'en' | 'it' | 'fr' | 'sv' | 'sq';

export interface StorefrontConfig {
  storefront: Storefront;
  currency: Currency;
  currencySymbol: string;
  flag: string;
  countryCode: 'CH' | 'DE' | 'AT' | 'IT' | 'SE' | 'AL';
  domain: string;
  defaultLocale: StorefrontLocale;
}

/**
 * Detects the active storefront based on window.location.hostname.
 * - bigfollow.de / *.bigfollow.de → DE (EUR, de)
 * - bigfollow.at / *.bigfollow.at → AT (EUR, de)
 * - bigfollow.it / *.bigfollow.it → IT (EUR, it)
 * - bigfollow.se / *.bigfollow.se → SE (SEK, sv)
 * - bigfollow.al / *.bigfollow.al → AL (EUR, sq)  -- ALL/Lek later without rebuild
 * - everything else (localhost, lovable.app, bigfollow.ch) → CH (CHF, de)
 *
 * SSR-safe: returns 'ch' when window is undefined.
 */
export function detectStorefront(hostname?: string): Storefront {
  // P2.6 §5 — highest-priority override written by the AT boot-script in
  // index.html BEFORE the module bundle runs. Only set when the real host is
  // bigfollow.at (or *.bigfollow.at). Never set on bigfollow.ch or previews,
  // so CH remains byte-identical and the ?sf=at preview flag stays the sole
  // AT trigger on preview hosts.
  if (typeof window !== 'undefined') {
    const forced = (window as any).__BF_FORCE_SF__;
    if (forced === 'at' || forced === 'ch') return forced as Storefront;
  }
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  if (!host) return 'ch';
  if (host === 'bigfollow.de' || host.endsWith('.bigfollow.de')) return 'de';
  // Includes AT_RENDER_HOST (render-at.bigfollow.at) via the wildcard suffix.
  if (host === 'bigfollow.at' || host.endsWith('.bigfollow.at')) return 'at';
  if (host === 'bigfollow.it' || host.endsWith('.bigfollow.it')) return 'it';
  if (host === 'bigfollow.se' || host.endsWith('.bigfollow.se')) return 'se';
  if (host === 'bigfollow.al' || host.endsWith('.bigfollow.al')) return 'al';
  // P2.6 §4: AT preview-only override. Activated ONLY when
  //   1. VITE_PREVIEW_ENABLE_AT === 'true' (never set on prod build), AND
  //   2. host is a preview host (localhost / *.lovable.app / *.lovable.dev), AND
  //   3. session opted in via ?sf=at (persisted to sessionStorage 'preview_sf').
  // The prod domain bigfollow.ch NEVER matches the host guard, so the flag is a no-op there.
  if (isPreviewAtEnabled(host) && readPreviewSf() === 'at') return 'at';
  return 'ch';
}

function isPreviewAtEnabled(host: string): boolean {
  try {
    // Vite replaces `import.meta.env.VITE_*` at build/serve time via textual match
    // (optional chaining is NOT rewritten — must be dot access).
    const flag = import.meta.env.VITE_PREVIEW_ENABLE_AT === 'true';
    if (!flag) return false;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.lovable.app') ||
      host.endsWith('.lovable.dev')
    );
  } catch {
    return false;
  }
}

function readPreviewSf(): Storefront | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get('sf');
    if (q === 'at' || q === 'ch') {
      window.sessionStorage.setItem('preview_sf', q);
      return q as Storefront;
    }
    const stored = window.sessionStorage.getItem('preview_sf');
    if (stored === 'at' || stored === 'ch') return stored as Storefront;
  } catch {}
  return null;
}

const CONFIG: Record<Storefront, StorefrontConfig> = {
  ch: {
    storefront: 'ch',
    currency: 'CHF',
    currencySymbol: 'CHF',
    flag: '🇨🇭',
    countryCode: 'CH',
    domain: 'bigfollow.ch',
    defaultLocale: 'de',
  },
  de: {
    storefront: 'de',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇩🇪',
    countryCode: 'DE',
    domain: 'bigfollow.de',
    defaultLocale: 'de',
  },
  at: {
    storefront: 'at',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇦🇹',
    countryCode: 'AT',
    domain: 'bigfollow.at',
    defaultLocale: 'de',
  },
  it: {
    storefront: 'it',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇮🇹',
    countryCode: 'IT',
    domain: 'bigfollow.it',
    defaultLocale: 'it',
  },
  se: {
    storefront: 'se',
    currency: 'SEK',
    currencySymbol: 'kr',
    flag: '🇸🇪',
    countryCode: 'SE',
    domain: 'bigfollow.se',
    defaultLocale: 'sv',
  },
  al: {
    // Currency is EUR for launch; switch to 'ALL' + symbol 'L' later without any structural rebuild.
    storefront: 'al',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇦🇱',
    countryCode: 'AL',
    domain: 'bigfollow.al',
    defaultLocale: 'sq',
  },
};

/** All defined storefronts (for iteration, e.g. hreflang). */
export const ALL_STOREFRONTS: Storefront[] = ['ch', 'de', 'at', 'it', 'se', 'al'];

/**
 * Storefronts that are publicly live. New domains stay code-prepared but
 * invisible in UI/sitemap until explicitly promoted to LIVE.
 */
export const LIVE_STOREFRONTS: Storefront[] = ['ch', 'at'];

/**
 * Storefront -> display currency. Single source of truth.
 *
 * KEEP IN SYNC (SSOT) with:
 *   - CONFIG[...].currency below (same values)
 *   - Trigger `public.pvp_validate_currency()` in the DB (product_variation_prices)
 *   - `formatPriceForCurrency` in src/lib/formatPrice.ts (must handle every currency here)
 *   - `roundPrice` strategies in src/lib/priceRounding.ts
 *
 * When you change a mapping (e.g. AL: EUR → ALL), update ALL of the above in
 * the same change or displayed prices and stored overrides will drift.
 */
export const STOREFRONT_CURRENCY: Record<Storefront, Currency> = {
  ch: 'CHF',
  de: 'EUR',
  at: 'EUR',
  it: 'EUR',
  se: 'SEK',
  al: 'EUR', // NOTE: AL uses EUR at launch. Trigger mirrors this.
};

/**
 * Returns the canonical https base URL to use for SEO tags (canonical, og:url,
 * hreflang, JSON-LD). Non-live storefronts fall back to the CH domain so that
 * dormant domains can never leak fake canonicals or split ranking signals.
 */
export function getActiveSiteUrl(sf?: Storefront): string {
  const active = sf ?? detectStorefront();
  const cfg = CONFIG[active];
  if (LIVE_STOREFRONTS.includes(active)) return `https://${cfg.domain}`;
  return 'https://bigfollow.ch';
}

export function useStorefront(): StorefrontConfig {
  return useMemo(() => CONFIG[detectStorefront()], []);
}

export function getStorefrontConfig(sf: Storefront): StorefrontConfig {
  return CONFIG[sf];
}